import { useModalStatusMessage } from '@/hooks';
import { useGlobalModal } from '@/hooks/useGlobalModal';
import { useMutation, useQuery } from '@apollo/client';
import GlobalModal from '@components/GlobalModal';
import ModalActions from '@components/modal/ModalActions';
import ModalContent from '@components/modal/ModalContent';
import ModalTitle from '@components/modal/ModalTitle';
import { msg, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Skeleton,
  Tooltip,
  Typography,
} from '@mui/material';
import { type FC } from 'react';
import { GET_DEVICE_CONFIGURATION_EXECUTION_STATE } from '../graphql/queries/getDeviceConfigurationExecutionState';
import { TRIGGER_CONFIG_FILE_UPDATE } from '../graphql/mutations/triggerConfigFileUpdate';

const RUNNING_STATUS = 'RUNNING';

const getStatusLabel = (_: ReturnType<typeof useLingui>['_'], status: string) => {
  const labels: Record<string, string> = {
    RUNNING: _(msg`En ejecución`),
    SUCCEEDED: _(msg`Exitoso`),
    FAILED: _(msg`Fallido`),
    ABORTED: _(msg`Abortado`),
    UNKNOWN: _(msg`Desconocida`),
  };
  return labels[status] ?? status;
};

const getStatusColor = (status: string): 'warning' | 'success' | 'error' | 'default' => {
  switch (status) {
    case 'RUNNING': return 'warning';
    case 'SUCCEEDED': return 'success';
    case 'FAILED':
    case 'ABORTED': return 'error';
    default: return 'default';
  }
};

interface ComponentProps {
  deviceId: string;
  onClose: () => void;
}

const Component: FC<ComponentProps> = ({ deviceId, onClose }) => {
  const { _ } = useLingui();
  const { openModalErrorMessage, openModalSuccessMessage } = useModalStatusMessage();

  const { data, loading, refetch } = useQuery(GET_DEVICE_CONFIGURATION_EXECUTION_STATE, {
    variables: { deviceId },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });

  const [triggerUpdate, { loading: mutationLoading }] = useMutation(TRIGGER_CONFIG_FILE_UPDATE, {
    onCompleted(data) {
      openModalSuccessMessage(
        _(msg`Actualización iniciada. Nombre de ejecución: ${data.triggerConfigFileUpdate.executionName}`),
      );
      refetch();
    },
    onError(error) {
      openModalErrorMessage(error.message);
    },
    refetchQueries: [GET_DEVICE_CONFIGURATION_EXECUTION_STATE]
  });

  const status = data?.retrieveDeviceConfigurationExecutionState?.status;
  const isRunning = status === RUNNING_STATUS;

  const handleTrigger = () => {
    triggerUpdate({ variables: { deviceId } });
  };

  return (
    <>
      <ModalTitle onClose={onClose} title={_(msg`Actualizar archivo de configuración`)} />
      <ModalContent dividers>
        <Box display="flex" flexDirection="column" gap={3}>
          <Box>
            <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
              <Box display="flex" alignItems="center" gap={1}>
                <Typography variant="h6">
                  <Trans>Estado de última actualización</Trans>
                </Typography>
              </Box>
              {loading && !status && <Skeleton variant="rounded" width={100} height={28} />}

              {status && (
                <Box display="flex" alignItems="center" gap={1}>

                  <Tooltip title={_(msg`Estado`)}>
                    <Chip
                      label={getStatusLabel(_, status)}
                      color={getStatusColor(status)}
                      size="small"
                      sx={{ fontWeight: 600 }}
                    />
                  </Tooltip>
                </Box>
              )}

              <Tooltip title={_(msg`Actualizar estado`)}>
                <span>
                  <IconButton size="small" onClick={() => refetch()} disabled={loading}>
                    {loading ? (
                      <CircularProgress size={16} />
                    ) : (
                      <RefreshIcon fontSize="small" />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
            </Box>

            {!loading && !status && (
              <Trans>No hay información de estado disponible</Trans>
            )}

            {isRunning && (
              <Alert severity="warning">
                <Trans>
                  Hay una ejecución en curso. No es posible disparar una nueva actualización hasta que
                  finalice.
                </Trans>
              </Alert>
            )}
          </Box>

        </Box>
      </ModalContent>
      <ModalActions
        onClose={onClose}
        onSubmit={handleTrigger}
        submitLabel={_(msg`Disparar actualización`)}
        submitDisabled={isRunning || mutationLoading || loading}
        submitLoading={mutationLoading}
      />
    </>
  );
};

export const TriggerConfigUpdateModal: FC = () => {
  const { globalModalProps, state, onClose } = useGlobalModal('triggerConfigUpdateModal');

  return (
    <GlobalModal {...globalModalProps} dialogProps={{ maxWidth: 'sm' }}>
      {state.open && <Component deviceId={state.deviceId} onClose={onClose} />}
    </GlobalModal>
  );
};

export default TriggerConfigUpdateModal;
